"use client";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faXmark,
  faCircleInfo,
} from "@awesome.me/kit-c10ef98ed3/icons/classic/regular";
import { useState, useEffect, useCallback, useRef } from "react";
import { Button, IconButton, useMediaQuery } from "@mui/material";
import { useTranslations } from "next-intl";
import CustomSwipeableDrawer from "@/components/CustomSwipeableDrawer/CustomSwipeableDrawer";
import TreatmentCardCarousel from "@/components/ResultsPage/SymptomResultsSection/TreatmentCards/TreatmentCardCarousel";
import { Variant } from "@/hooks/useVariant";
import { trackCtaClick } from "@/utils/commonEvents";
import type { ExplainabilityBullet } from "@/api/types";

export interface CardComponentProps {
  cardId: string;
  variant: Variant;
  riskLabelKey: string;
  titleKey: string;
  descriptionIntroKey: string;
  imageUrl: string;
  ctaLabelKey: string;
  expandedDescription: React.ReactNode;
  treatmentOptionsLabelKey?: string;
  explainabilityBullets?: ExplainabilityBullet[];
}

export default function CardComponent({
  cardId,
  variant,
  riskLabelKey,
  titleKey,
  descriptionIntroKey,
  imageUrl,
  ctaLabelKey,
  expandedDescription,
  treatmentOptionsLabelKey,
  explainabilityBullets,
}: CardComponentProps) {
  const t = useTranslations("results-page.symptom-results-section");
  const tAria = useTranslations("aria");

  const isMobile = useMediaQuery("(max-width:1024px)");
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTreatmentOpen, setIsTreatmentOpen] = useState(false);
  const panelContentRef = useRef<HTMLDivElement>(null);

  // Reset expanded state on breakpoint change
  useEffect(() => {
    setIsExpanded(false);
    setIsTreatmentOpen(false);
  }, [isMobile]);

  const handleCtaClick = () => {
    trackCtaClick({
      componentName: "sleep_assessment_pro",
      clickedArea: t(ctaLabelKey),
      clickedText: t(ctaLabelKey),
    });
    panelContentRef.current?.scrollTo(0, 0);
    setIsExpanded(true);
  };

  const handleTreatmentOptionsClick = () => {
    trackCtaClick({
      componentName: "sleep_assessment_pro",
      clickedArea: treatmentOptionsLabelKey ? t(treatmentOptionsLabelKey) : "Treatment options",
      clickedText: treatmentOptionsLabelKey ? t(treatmentOptionsLabelKey) : "Treatment options",
    });
    setIsTreatmentOpen(true);
  };

  const handleClose = useCallback(() => {
    setIsExpanded(false);
  }, []);

  const isExpandedDesktop = isExpanded && !isMobile;

  return (
    <div className="card-component-wrapper">
      <div
        className={`card-component${isExpandedDesktop ? " card-component--expanded" : ""}`}
      >
        <div
          className={`card-component-image-container card-component-image-container--${cardId}`}
        >
          <img className="card-component-image" src={imageUrl} alt="" />
        </div>

        {/* Dark image overlay — fades in when expanded */}
        <div className="card-component-overlay" />

        <div className="card-component-header">
          <p className="card-component-risk-label">{t(riskLabelKey)}</p>
          <h3 className="card-component-title">{t(titleKey)}</h3>
        </div>

        <div className="card-component-description">
          {t(descriptionIntroKey)}
          <ul>
            {explainabilityBullets && explainabilityBullets.length > 0
              ? explainabilityBullets.map((bullet, index) => (
                  <li key={`${bullet.key}-${index}`}>
                    {t(bullet.key, bullet.params)}
                  </li>
                ))
              : null}
          </ul>
        </div>

        <div className="card-component-cta-area">
          <Button
            variant="contained"
            color="secondary"
            className={
              variant === Variant.FEMALE
                ? "card-component-cta-button--female"
                : undefined
            }
            onClick={handleCtaClick}
            endIcon={
              <FontAwesomeIcon icon={faPlus} aria-hidden focusable={false} />
            }
          >
            {t(ctaLabelKey)}
          </Button>
        </div>

        {/* Desktop expanded — inert keeps content unfocusable when collapsed */}
        <div
          className="card-component-panel-wrapper"
          aria-hidden={!isExpandedDesktop}
          inert={!isExpandedDesktop || undefined}
        >
          <div className="card-component-panel">
            <div className="card-component-panel-content" ref={panelContentRef}>
              {expandedDescription}
            </div>
            <div className="card-component-panel-footer">
              {treatmentOptionsLabelKey && (
                <Button
                  variant="text"
                  className="card-component-treatment-link"
                  onClick={handleTreatmentOptionsClick}
                  endIcon={
                    <FontAwesomeIcon
                      icon={faCircleInfo}
                      aria-hidden
                      focusable={false}
                    />
                  }
                >
                  {t(treatmentOptionsLabelKey)}
                </Button>
              )}
              <IconButton onClick={handleClose} aria-label={tAria("close")}>
                <FontAwesomeIcon icon={faXmark} aria-hidden focusable={false} />
              </IconButton>
            </div>
          </div>
        </div>
      </div>

      {/* ── Mobile expanded ── */}
      <CustomSwipeableDrawer
        isOpen={isExpanded && isMobile}
        onClose={handleClose}
      >
        <div className="card-component-sheet-title-area">
          <h3 className="card-component-sheet-title">{t(titleKey)}</h3>
        </div>
        <div className="card-component-sheet-body">{expandedDescription}</div>
        {treatmentOptionsLabelKey && (
          <div className="card-component-sheet-footer">
            <Button
              variant="text"
              className="card-component-treatment-link"
              onClick={handleTreatmentOptionsClick}
              endIcon={
                <FontAwesomeIcon
                  icon={faCircleInfo}
                  aria-hidden
                  focusable={false}
                />
              }
            >
              {t(treatmentOptionsLabelKey)}
            </Button>
          </div>
        )}
      </CustomSwipeableDrawer>

      {/* ── Treatment Options Drawer ── */}
      {treatmentOptionsLabelKey && (
        <CustomSwipeableDrawer
          isOpen={isTreatmentOpen}
          onClose={() => setIsTreatmentOpen(false)}
        >
          <TreatmentCardCarousel />
        </CustomSwipeableDrawer>
      )}
    </div>
  );
}
